import React, { createContext, useContext, useReducer, useEffect, useState, useCallback } from 'react';
import { calculateCartTotalWithDiscounts } from '../services/pricingService';

// localStorage key for cart persistence
const CART_STORAGE_KEY = 'atelier-ambree-cart';

// Helper functions for localStorage operations
const saveCartToStorage = (cartItems) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return [];
  }
};

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          ),
          showCartDropdown: true,
          autoHideCartDropdown: true
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload }],
        showCartDropdown: true,
        autoHideCartDropdown: true
      };
    
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };
    
    case 'UPDATE_ITEM_STOCK':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, stock_quantity: action.payload.stock_quantity }
            : item
        )
      };
    
    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };
    
    case 'SHOW_CART_DROPDOWN':
      return {
        ...state,
        showCartDropdown: true,
        autoHideCartDropdown: false
      };
    
    case 'HIDE_CART_DROPDOWN':
      return {
        ...state,
        showCartDropdown: false,
        autoHideCartDropdown: false
      };
    
    case 'CANCEL_AUTO_HIDE':
      return {
        ...state,
        autoHideCartDropdown: false
      };
    
    case 'SHOW_MOBILE_CART_MODAL':
      return {
        ...state,
        showMobileCartModal: true
      };
    
    case 'HIDE_MOBILE_CART_MODAL':
      return {
        ...state,
        showMobileCartModal: false
      };
    
    case 'LOAD_CART_FROM_STORAGE':
      return {
        ...state,
        items: action.payload || []
      };
    
    default:
      return state;
  }
};

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, { 
    items: [],
    showCartDropdown: false,
    autoHideCartDropdown: false,
    showMobileCartModal: false
  });
  const [cartTotals, setCartTotals] = useState({
    subtotal: 0,
    shipping: 0,
    total: 0
  });

  // Load cart from localStorage on component mount
  useEffect(() => {
    const savedCartItems = loadCartFromStorage();
    if (savedCartItems.length > 0) {
      dispatch({ type: 'LOAD_CART_FROM_STORAGE', payload: savedCartItems });
    }
  }, []);

  const addToCart = useCallback((product) => {
    dispatch({ type: 'ADD_TO_CART', payload: product });
    
    // Check if we're on mobile and show mobile cart modal
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      dispatch({ type: 'SHOW_MOBILE_CART_MODAL' });
    }
  }, []);

  const removeFromCart = useCallback((productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  }, []);

  const updateQuantityWithStockCheck = useCallback(async (productId, quantity) => {
    // Find the item in the cart
    const cartItem = state.items.find(item => item.id === productId);
    if (!cartItem) return;

    // If the item has stock_quantity, validate against it
    if (cartItem.stock_quantity !== undefined && cartItem.stock_quantity > 0) {
      if (quantity > cartItem.stock_quantity) {
        // Don't allow quantity to exceed stock
        return;
      }
    }

    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  }, [state.items]);

  const updateCartItemStock = useCallback((productId, stockQuantity) => {
    dispatch({ type: 'UPDATE_ITEM_STOCK', payload: { id: productId, stock_quantity: stockQuantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
    // Also clear from localStorage
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  }, []);

  const getCartTotal = useCallback(() => {
    return cartTotals.total || 0;
  }, [cartTotals.total]);

  const getCartSubtotal = useCallback(() => {
    return cartTotals.subtotal || 0;
  }, [cartTotals.subtotal]);

  const getCartShipping = useCallback(() => {
    return cartTotals.shipping || 0;
  }, [cartTotals.shipping]);

  const getCartItemsCount = useCallback(() => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  }, [state.items]);

  const getDiscountItems = useCallback(() => {
    console.log('getDiscountItems called, cartTotals:', cartTotals);
    console.log('discountItems:', cartTotals.discountItems);
    return cartTotals.discountItems || [];
  }, [cartTotals.discountItems]);

  const getCartDiscount = useCallback(() => {
    const discountItems = cartTotals.discountItems || [];
    return discountItems.reduce((total, item) => total + (item.amount || 0), 0);
  }, [cartTotals.discountItems]);

  const clearCartStorage = useCallback(() => {
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
      console.log('Cart storage cleared');
    } catch (error) {
      console.error('Error clearing cart storage:', error);
    }
  }, []);

  // Save cart to localStorage whenever items change
  useEffect(() => {
    saveCartToStorage(state.items);
  }, [state.items]);

  // Recalculate cart totals when items change
  useEffect(() => {
    const recalculateTotals = async () => {
      if (state.items.length === 0) {
        setCartTotals({ subtotal: 0, shipping: 0, total: 0, discountItems: [] });
        return;
      }

      try {
        const totals = await calculateCartTotalWithDiscounts(state.items);
        console.log('Setting cart totals:', totals);
        setCartTotals(totals);
      } catch (error) {
        console.error('Error calculating cart totals:', error);
        // Fall back to simple calculation
        const subtotal = state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
        setCartTotals({ subtotal, shipping: 0, total: subtotal, discountItems: [] });
      }
    };

    recalculateTotals();
  }, [state.items]);

  const showCartDropdownAction = useCallback(() => {
    dispatch({ type: 'SHOW_CART_DROPDOWN' });
  }, []);

  const hideCartDropdownAction = useCallback(() => {
    dispatch({ type: 'HIDE_CART_DROPDOWN' });
  }, []);

  const cancelAutoHideAction = useCallback(() => {
    dispatch({ type: 'CANCEL_AUTO_HIDE' });
  }, []);

  const showMobileCartModalAction = useCallback(() => {
    dispatch({ type: 'SHOW_MOBILE_CART_MODAL' });
  }, []);

  const hideMobileCartModalAction = useCallback(() => {
    dispatch({ type: 'HIDE_MOBILE_CART_MODAL' });
  }, []);

  // Note: Removed scroll-to-top behavior for mobile since we now use mobile cart modal

  // Auto-hide cart dropdown after 5 seconds when items are added
  useEffect(() => {
    if (state.showCartDropdown && state.autoHideCartDropdown) {
      const timer = setTimeout(() => {
        dispatch({ type: 'HIDE_CART_DROPDOWN' });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [state.showCartDropdown, state.autoHideCartDropdown]);

  const value = {
    items: state.items,
    showCartDropdown: state.showCartDropdown,
    showMobileCartModal: state.showMobileCartModal,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateQuantityWithStockCheck,
    updateCartItemStock,
    clearCart,
    clearCartStorage,
    getCartTotal,
    getCartSubtotal,
    getCartShipping,
    getCartItemsCount,
    getDiscountItems,
    getCartDiscount,
    showCartDropdownAction,
    hideCartDropdownAction,
    cancelAutoHideAction,
    showMobileCartModalAction,
    hideMobileCartModalAction
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
} 